import { GRAVITY_TILES_PER_S2, MAX_FALL_SPEED_TILES_PER_S } from '@md/shared';
import type { Body } from './body.js';
import { type CollisionGrid, type MoveResult, moveBody, type Obstacle } from './world.js';

/** Paramètres d'intégration d'un tick (tous en unités tuiles / secondes). */
export interface IntegrationOptions {
  readonly dt: number;
  readonly gravity?: number;
  readonly maxFallSpeed?: number;
  /** Boîtes solides mobiles à prendre en compte (portes, blocs, ascenseurs). */
  readonly obstacles?: readonly Obstacle[];
}

/**
 * Avance un corps d'un pas de temps FIXE puis résout ses collisions.
 *
 * L'intégration verticale est trapézoïdale — `dy = (v + v')/2 · dt` — ce qui
 * est **exact** pour une accélération constante : le sommet d'un saut vaut
 * précisément `v²/(2g)` (`JUMP_APEX_TILES`), ce qui rend les assertions de
 * tests indépendantes du pas de temps. L'horizontale est à vitesse constante
 * sur le tick, donc exacte elle aussi.
 */
export function integrateBody(
  body: Body,
  grid: CollisionGrid,
  options: IntegrationOptions,
): MoveResult {
  const { dt } = options;
  const gravity = options.gravity ?? GRAVITY_TILES_PER_S2;
  const maxFallSpeed = options.maxFallSpeed ?? MAX_FALL_SPEED_TILES_PER_S;

  const nextVy = Math.min(body.vy + gravity * dt, maxFallSpeed);
  const dy = 0.5 * (body.vy + nextVy) * dt;
  const dx = body.vx * dt;
  body.vy = nextVy;

  const result = moveBody(body, grid, dx, dy, options.obstacles);

  if (result.hitX) {
    body.vx = 0;
  }
  if (result.landed || result.hitCeiling) {
    body.vy = 0;
  }
  body.onGround = result.landed;

  return result;
}
